import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Info } from "lucide-react";
import { useWizard } from "../../context/WizardContext";
import { COVERAGE_SOURCE } from "../../types/individual.types";
import type { HealthCoverage } from "../../types/individual.types";

const COVERAGE_LABELS: Record<string, string> = {
  [COVERAGE_SOURCE.SELF]: "Self-purchased",
  [COVERAGE_SOURCE.EMPLOYER]: "Employer",
  [COVERAGE_SOURCE.MARKETPLACE]: "Marketplace",
};

function getHealth(list: HealthCoverage[] | undefined, personType: "TAXPAYER" | "SPOUSE" | "DEPENDENT", depIndex?: number): HealthCoverage | undefined {
  if (!list) return undefined;
  if (personType === "TAXPAYER") return list.find((p) => p.personType === "TAXPAYER");
  if (personType === "SPOUSE") return list.find((p) => p.personType === "SPOUSE");
  if (personType === "DEPENDENT" && depIndex !== undefined) {
    const deps = list.filter((p) => p.personType === "DEPENDENT");
    return deps[depIndex];
  }
  return undefined;
}

function ensureHealthList(list: HealthCoverage[] | undefined, personType: "TAXPAYER" | "SPOUSE" | "DEPENDENT", depIndex?: number): HealthCoverage {
  const arr = list ?? [];
  if (personType === "TAXPAYER") {
    let p = arr.find((x) => x.personType === "TAXPAYER");
    if (!p) {
      p = { personType: "TAXPAYER" };
      arr.push(p);
    }
    return p;
  }
  if (personType === "SPOUSE") {
    let p = arr.find((x) => x.personType === "SPOUSE");
    if (!p) {
      p = { personType: "SPOUSE" };
      arr.push(p);
    }
    return p;
  }
  if (personType === "DEPENDENT" && depIndex !== undefined) {
    const deps = arr.filter((x) => x.personType === "DEPENDENT");
    while (deps.length <= depIndex) {
      const newEntry = { personType: "DEPENDENT" as const };
      deps.push(newEntry);
      arr.push(newEntry);
    }
    return deps[depIndex];
  }
  return { personType: "TAXPAYER" };
}

export function IndividualStep3Health() {
  const { individualData, updateIndividualData } = useWizard();
  const list = individualData.healthCoverageInformation ?? [];
  const personalList = individualData.personalInformation ?? [];
  const hasSpouse = personalList.some((p) => p.personType === "SPOUSE");

  const setOne = (personType: "TAXPAYER" | "SPOUSE" | "DEPENDENT", depIndex: number | undefined, patch: Partial<HealthCoverage>) => {
    const arr = [...list];
    const p = ensureHealthList(arr, personType, depIndex);
    Object.assign(p, patch);
    if (!arr.find((x) => x.personType === p.personType && (personType !== "DEPENDENT" || x === p))) {
      if (personType === "DEPENDENT" && depIndex !== undefined) {
        const deps = arr.filter((x) => x.personType === "DEPENDENT");
        if (!deps[depIndex]) deps[depIndex] = { ...p, personType: "DEPENDENT" };
        else Object.assign(deps[depIndex], patch);
      } else {
        arr.push({ ...p, personType });
      }
    }
    updateIndividualData({ healthCoverageInformation: arr });
  };

  const tabs = [
    { value: "taxpayer", label: "Taxpayer" },
    ...(hasSpouse ? [{ value: "spouse", label: "Spouse" }] : []),
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-foreground">Health Coverage</h2>
        <p className="text-sm text-muted-foreground">
          Health care coverage for each family member.
        </p>
      </div>

      <Alert variant="info">
        <Info className="h-4 w-4" />
        <AlertDescription>
          Marketplace coverage → have Form 1095-A ready. Employer/other → have Form 1095-B ready.
        </AlertDescription>
      </Alert>

      <Tabs defaultValue="taxpayer" className="w-full">
        <TabsList className="border border-primary/30 bg-primary/5">
          {tabs.map((t) => (
            <TabsTrigger
              key={t.value}
              value={t.value}
              className={
                t.value === "taxpayer" || t.value === "spouse"
                  ? "data-[state=active]:bg-black data-[state=active]:text-white data-[state=active]:font-semibold data-[state=active]:shadow-sm data-[state=active]:border-black"
                  : undefined
              }
            >
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {tabs.map((tab) => {
          const isDep = tab.value.startsWith("dep");
          const depIndex = isDep ? parseInt(tab.value.replace("dep", ""), 10) - 1 : undefined;
          const personType = tab.value === "taxpayer" ? "TAXPAYER" : tab.value === "spouse" ? "SPOUSE" : "DEPENDENT";
          const h = getHealth(list, personType, depIndex) ?? { personType };
          return (
            <TabsContent key={tab.value} value={tab.value} className="mt-4">
              <Card>
                <CardContent className="pt-6 space-y-4">
                  <div className="space-y-2">
                    <Label>Any health care coverage?</Label>
                    <RadioGroup
                      value={h.hasHealthCoverage === true ? "yes" : h.hasHealthCoverage === false ? "no" : ""}
                      onValueChange={(v) =>
                        setOne(personType, depIndex, { hasHealthCoverage: v === "yes" })
                      }
                      className="flex gap-4"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="yes" id={`${tab.value}-h-yes`} />
                        <Label htmlFor={`${tab.value}-h-yes`} className="font-normal">
                          Yes
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="no" id={`${tab.value}-h-no`} />
                        <Label htmlFor={`${tab.value}-h-no`} className="font-normal">
                          No
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>
                  {h.hasHealthCoverage && (
                    <div className="space-y-2">
                      <Label>Coverage source</Label>
                      <Select
                        value={h.coverageSource ?? ""}
                        onValueChange={(v) =>
                          setOne(personType, depIndex, {
                            coverageSource: v as keyof typeof COVERAGE_SOURCE,
                          })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select source" />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(COVERAGE_LABELS).map(([value, label]) => (
                            <SelectItem key={value} value={value}>
                              {label}
                            </SelectItem>
                          ))}
                          <SelectItem value="OTHER">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          );
        })}
      </Tabs>
    </div>
  );
}
